import { useState, useEffect } from "react";
import { CONTRACT_TEMPLATES } from "./ContractsTab";
import CallBrief from "./CallBrief";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const TIER_LABELS = { distressed: "Distressed / Receivership", institutional: "Institutional PM", sfr: "Single-Family Rental", housing_authority: "Housing Authority", local: "Local DFW Operators", grassroots: "Grassroots — Start This Week", zillow: "Zillow / Neighborhood Targets", networking: "Networking Organizations" };
const TIER_COLORS = { distressed: "#FF6B35", institutional: "#4FC3F7", sfr: "#00FFFF", housing_authority: "#CE93D8", local: "#8BC34A", grassroots: "#FFC107", zillow: "#4CAF50", networking: "#9C27B0" };

const PACKAGE_TO_PITCH = {
  receivership: "pitch_distressed",
  institutional: "pitch_institutional",
  sfr: "pitch_sfr",
  housing_authority_dha: "dha_rfp",
  housing_authority_fwhs: "pitch_fwhs",
  local_pm: "pitch_grassroots",
};

const AVG_RENT_DFW = 1500;

const generatePersonalizedPitch = (prospect) => {
  const pt = prospect.package_type;
  let pitchKey = PACKAGE_TO_PITCH[pt] || "pitch_grassroots";
  if (pt === "housing_authority") {
    pitchKey = prospect.prospect_id === "dha" ? "dha_rfp" : "pitch_fwhs";
  }
  const tmpl = CONTRACT_TEMPLATES[pitchKey];
  if (!tmpl) return { subject: "", body: "", pitchKey };

  const name = (prospect.decision_maker || "").split("—")[0].split("–")[0].split("|")[0].trim() || "Hiring Manager";
  const company = prospect.company || "";
  const units = prospect.units || 0;
  const address = prospect.address || "";
  const vacancyCostDay = units > 0 ? Math.round(units * (AVG_RENT_DFW / 30)) : 0;
  const vacancyCostMonth = units > 0 ? (units * AVG_RENT_DFW).toLocaleString() : "0";
  const annualNOIRecovery = units > 0 ? (units * AVG_RENT_DFW * 0.6 * 12).toLocaleString() : "0";

  let fullText = tmpl.sections.map(s => {
    let heading = s.heading;
    let body = s.body;
    body = body.replace(/\[Name\]/g, name);
    body = body.replace(/\[Address\]/g, address);
    body = body.replace(/\[Rent\]/g, `$${AVG_RENT_DFW.toLocaleString()}`);
    body = body.replace(/\[Company\]/g, company);
    return heading + "\n\n" + body;
  }).join("\n\n---\n\n");

  let subject = tmpl.sections[0]?.heading || tmpl.title;
  if (subject.startsWith("SUBJECT:")) {
    subject = subject.replace("SUBJECT:", "").trim();
  }
  subject = subject.replace(/\[Name\]/g, name).replace(/\[Company\]/g, company);

  const statsBlock = units > 0
    ? `\n\n--- COST OF DOING NOTHING: ${company.toUpperCase()} ---\nPortfolio: ${units.toLocaleString()} units\nAssuming 10% vacancy (${Math.round(units * 0.1)} units empty):\n  Daily vacancy loss: $${(Math.round(units * 0.1) * Math.round(AVG_RENT_DFW / 30)).toLocaleString()}/day\n  Monthly vacancy loss: $${(Math.round(units * 0.1) * AVG_RENT_DFW).toLocaleString()}/month\n  Annual vacancy loss: $${(Math.round(units * 0.1) * AVG_RENT_DFW * 12).toLocaleString()}/year\n\n--- WITH MOOVIN FORWARD (60% TURN TIME COMPRESSION) ---\n  Monthly vacancy loss reduced to: $${(Math.round(units * 0.1) * AVG_RENT_DFW * 0.4).toLocaleString()}/month\n  Monthly savings: $${(Math.round(units * 0.1) * AVG_RENT_DFW * 0.6).toLocaleString()}\n  Annual NOI recovery: $${(Math.round(units * 0.1) * AVG_RENT_DFW * 0.6 * 12).toLocaleString()}\n\n--- THE NET 30 GRIDLOCK ---\nTraditional vendors: Submit PO → Wait for approval → Wait for Net 30 payment → Order materials → Wait for delivery → Schedule labor = 30-45 days before work begins\nMoovin Forward: Materials funded and on-site Day 1 → Labor starts immediately → Unit rent-ready in 14-18 days\nDifference: 27+ days of rent recovered per unit\n`
    : "";

  return { subject, body: fullText + statsBlock, pitchKey, name, company, units };
};

const ProspectsTab = ({ setTab } = {}) => {
  const [prospects, setProspects] = useState([]);
  const [sentLog, setSentLog] = useState({});
  const [expanded, setExpanded] = useState(null);
  const [sendModal, setSendModal] = useState(null);
  const [sendForm, setSendForm] = useState({ contact_name: "", contact_email: "" });
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [pitchModal, setPitchModal] = useState(null);
  const [pitchData, setPitchData] = useState(null);
  const [pitchCopied, setPitchCopied] = useState(false);
  const [linkedinCopied, setLinkedinCopied] = useState(null);
  const [callBriefProspect, setCallBriefProspect] = useState(null);
  const [pitchEmailMode, setPitchEmailMode] = useState(false);
  const [pitchEmailForm, setPitchEmailForm] = useState({ to_email: "" });
  const [pitchSending, setPitchSending] = useState(false);
  const [pitchEmailResult, setPitchEmailResult] = useState(null);
  const [prospectFollowUps, setProspectFollowUps] = useState([]);
  const [pipelineMap, setPipelineMap] = useState({});
  const [viewMode, setViewMode] = useState("tiers");
  const [marketData, setMarketData] = useState(null);
  const [todaysCalls, setTodaysCalls] = useState([]);
  const [todaysCallsLoading, setTodaysCallsLoading] = useState(false);
  const [showAddProspect, setShowAddProspect] = useState(false);
  const [newProspect, setNewProspect] = useState({
    company: "", tier: "local_pm", units: "", address: "", status: "",
    pain_point: "", talking_points: "", decision_maker: "",
    primary_contact_name: "", primary_contact_phone: "",
    primary_contact_email: "", primary_contact_role: ""
  });
  const [savingProspect, setSavingProspect] = useState(false);

  const PIPELINE_STAGES = [
    { key: "cold", label: "Cold", color: "#888" },
    { key: "contacted", label: "Contacted", color: "#4FC3F7" },
    { key: "responded", label: "Responded", color: "#FFC107" },
    { key: "meeting", label: "Meeting", color: "#FF6B35" },
    { key: "won", label: "Won", color: "#4CAF50" },
    { key: "lost", label: "Lost", color: "#FF4444" },
  ];

  const parseContact = (prospect) => {
    const dm = prospect.decision_maker || "";
    const emailMatch = dm.match(/[\w.-]+@[\w.-]+\.\w+/);
    const nameMatch = dm.split("—")[0].split("|")[0].split("–")[0].trim();
    return {
      contact_name: nameMatch || "",
      contact_email: emailMatch ? emailMatch[0] : ""
    };
  };

  useEffect(() => {
    Promise.all([
      fetch(`${BACKEND_URL}/api/admin/prospects`, { credentials: "include" }).then(r => r.ok ? r.json() : { prospects: [], sent_log: {} }),
      fetch(`${BACKEND_URL}/api/admin/prospect-followups`, { credentials: "include" }).then(r => r.ok ? r.json() : []),
      fetch(`${BACKEND_URL}/api/admin/prospect-pipeline`, { credentials: "include" }).then(r => r.ok ? r.json() : {}),
      fetch(`${BACKEND_URL}/api/market-data`).then(r => r.ok ? r.json() : null)
    ]).then(([prospectData, fups, pipeline, market]) => {
      setProspects(prospectData.prospects);
      setSentLog(prospectData.sent_log);
      if (Array.isArray(fups)) setProspectFollowUps(fups);
      setPipelineMap(pipeline || {});
      if (market) setMarketData(market);
    });
  }, []);

  const saveNewProspect = async () => {
    if (!newProspect.company.trim()) {
      alert("Company name is required");
      return;
    }
    setSavingProspect(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/prospects/custom`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          ...newProspect,
          units: newProspect.units ? parseInt(newProspect.units, 10) : 0,
          package_type: newProspect.tier
        })
      });
      if (!r.ok) throw new Error("Save failed");
      const saved = await r.json();
      setProspects([saved, ...prospects]);
      setShowAddProspect(false);
      setNewProspect({
        company: "", tier: "local_pm", units: "", address: "", status: "",
        pain_point: "", talking_points: "", decision_maker: "",
        primary_contact_name: "", primary_contact_phone: "",
        primary_contact_email: "", primary_contact_role: ""
      });
    } catch (e) {
      alert("Failed to save prospect: " + e.message);
    }
    setSavingProspect(false);
  };

  const loadTodaysCalls = () => {
    setTodaysCallsLoading(true);
    fetch(`${BACKEND_URL}/api/admin/prospects/todays-calls`, { credentials: "include" })
      .then(r => r.ok ? r.json() : { calls: [] })
      .then(data => {
        setTodaysCalls(data.calls || []);
        setTodaysCallsLoading(false);
      })
      .catch(() => setTodaysCallsLoading(false));
  };

  // Auto-load today's calls when user switches into that view
  useEffect(() => {
    if (viewMode === "todays-calls") loadTodaysCalls();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode]);

  const sendPackage = async (e) => {
    e.preventDefault();
    if (!sendForm.contact_email) return;
    setSending(true);
    setSendResult(null);
    try {
      const r = await fetch(`${BACKEND_URL}/api/admin/prospects/${sendModal}/send`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify(sendForm)
      });
      const data = await r.json();
      setSendResult(data.sent ? "sent" : "logged");
      setSentLog(prev => ({ ...prev, [sendModal]: data.log }));
    } catch { setSendResult("error"); }
    setSending(false);
  };

  const openPitchModal = (prospect) => {
    const data = generatePersonalizedPitch(prospect);
    const c = parseContact(prospect);
    setPitchData(data);
    setPitchModal(prospect);
    setPitchCopied(false);
    setPitchEmailMode(false);
    setPitchEmailResult(null);
    setPitchEmailForm({ to_email: c.contact_email });
  };

  const sendPitchEmail = async (e) => {
    e.preventDefault();
    if (!pitchEmailForm.to_email || !pitchData) return;
    setPitchSending(true);
    setPitchEmailResult(null);
    try {
      const htmlContent = `<div style="font-family:Arial,sans-serif;line-height:1.6;color:#222;">${pitchData.body.split("\n").map(l => l === "---" ? "<hr>" : l ? `<p>${l}</p>` : "<br>").join("")}</div>`;
      const r = await fetch(`${BACKEND_URL}/api/admin/send-contract-email`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ to_email: pitchEmailForm.to_email, subject: pitchData.subject, html_content: htmlContent })
      });
      const data = await r.json();
      setPitchEmailResult(data.sent ? "sent" : data.forward_to_self ? "forwarded" : "logged");
    } catch { setPitchEmailResult("error"); }
    setPitchSending(false);
  };

  const moveProspect = async (prospectId, newStage) => {
    setPipelineMap(prev => ({ ...prev, [prospectId]: newStage }));
    try {
      await fetch(`${BACKEND_URL}/api/admin/prospect-pipeline/${prospectId}`, {
        method: "PUT", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ stage: newStage })
      });
    } catch (e) { console.error("Pipeline update failed:", e); }
  };

  const scheduleViaAutoPilot = (prospect) => {
    const c = parseContact(prospect);
    const pitch = generatePersonalizedPitch(prospect);
    const tierLabel = TIER_LABELS[prospect.tier] || prospect.tier || "";
    const teaser = (prospect.talking_points || "").split(/\.\s/).slice(0, 2).join(". ").trim();
    const message = [
      `${c.contact_name || "Hi there"} —`,
      "",
      `I lead Stabilization at Moovin Forward (DFW). For ${prospect.company} specifically: ${teaser}${teaser.endsWith(".") ? "" : "."}`,
      "",
      pitch.units > 0
        ? `On a portfolio your size, even a 60% turn-time compression recovers an estimated $${(Math.round(prospect.units * 0.1) * AVG_RENT_DFW * 0.6 * 12).toLocaleString()}/yr in NOI.`
        : "We deliver locked per-unit pricing, NSPIRE-compliant finishes, and audit-ready Line-Itemized Packets — no requisition delays, materials on-site Day 1.",
      "",
      "Worth a 15-min call? https://calendly.com/nivoomforward",
      "",
      "Shannon Ware",
      "Founder, Moovin Forward",
    ].join("\n");

    const prefill = {
      prospect_name: c.contact_name || prospect.company,
      prospect_email: c.contact_email || "",
      property_address: prospect.address || "",
      pitch_type: prospect.tier === "housing_authority" ? "agency_intro" : prospect.tier === "institutional" ? "reit_intro" : "manual",
      custom_message: message,
      meta_company: prospect.company,
      meta_tier: tierLabel,
    };
    try {
      sessionStorage.setItem("mf_autopilot_prefill", JSON.stringify(prefill));
    } catch {}
    if (typeof setTab === "function") {
      setTab("auto-pilot");
    } else {
      // Fallback: dispatch custom event
      window.dispatchEvent(new CustomEvent("mf-go-autopilot", { detail: prefill }));
    }
  };

  const tiers = [...new Set(prospects.map(p => p.tier))];

  return (
    <div className="dash-tab-content" data-testid="prospects-tab">
      <div className="prospects-header">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 16, flexWrap: "wrap" }}>
          <div style={{ flex: 1, minWidth: 250 }}>
            <h2 style={{ margin: 0 }}>Prospect Intelligence Center</h2>
            <p className="ops-lead" style={{ marginTop: 4 }}>Real DFW companies that need your ROI Stabilization Model. Research, talking points, and one-click outreach packages.</p>
          </div>
          <button
            onClick={() => setShowAddProspect(true)}
            className="prospect-pitch-btn"
            data-testid="add-prospect-btn"
            style={{
              background: "linear-gradient(135deg, #00FFFF 0%, #00C8C8 100%)",
              color: "#0A1628",
              fontWeight: 700,
              padding: "10px 18px"
            }}
          >
            <i className="fas fa-plus" style={{ marginRight: 6 }}></i>
            Add Prospect
          </button>
        </div>
        <div className="prospects-view-toggle" data-testid="prospects-view-toggle">
          <button className={`prospects-toggle-btn ${viewMode === "tiers" ? "active" : ""}`} onClick={() => setViewMode("tiers")} data-testid="view-tiers-btn">By Tier</button>
          <button className={`prospects-toggle-btn ${viewMode === "pipeline" ? "active" : ""}`} onClick={() => setViewMode("pipeline")} data-testid="view-pipeline-btn">Sales Pipeline</button>
          <button
            className={`prospects-toggle-btn ${viewMode === "todays-calls" ? "active" : ""}`}
            onClick={() => setViewMode("todays-calls")}
            data-testid="view-todays-calls-btn"
            style={{ position: "relative" }}
          >
            <i className="fas fa-phone-volume" style={{ marginRight: 6 }}></i>
            Today&apos;s Calls
            {todaysCalls.length > 0 && (
              <span style={{
                marginLeft: 8, background: "#FF6B35", color: "#fff",
                padding: "2px 7px", borderRadius: 10, fontSize: "0.7rem", fontWeight: 700
              }} data-testid="todays-calls-count">{todaysCalls.length}</span>
            )}
          </button>
        </div>
      </div>

      {/* DFW Market Pulse */}
      {marketData && (
        <div className="market-pulse" data-testid="market-pulse">
          <h3 className="market-pulse-title">DFW Market Pulse <span className="market-pulse-period">{marketData.data_period}</span></h3>
          <div className="market-pulse-grid">
            <div className="market-pulse-stat">
              <span className="market-pulse-val">{marketData.vacancy_rate}%</span>
              <span className="market-pulse-label">Vacancy Rate</span>
            </div>
            <div className="market-pulse-stat">
              <span className="market-pulse-val">${marketData.avg_rent?.toLocaleString()}</span>
              <span className="market-pulse-label">Avg Rent</span>
            </div>
            <div className="market-pulse-stat">
              <span className="market-pulse-val">{(marketData.offline_units / 1000).toFixed(0)}K</span>
              <span className="market-pulse-label">Offline Units</span>
            </div>
            <div className="market-pulse-stat">
              <span className="market-pulse-val">{(marketData.new_units_delivered / 1000).toFixed(1)}K</span>
              <span className="market-pulse-label">New Delivered</span>
            </div>
            <div className="market-pulse-stat">
              <span className="market-pulse-val">${marketData.foreclosure_pipeline}M+</span>
              <span className="market-pulse-label">Foreclosure Pipeline</span>
            </div>
            <div className="market-pulse-stat">
              <span className="market-pulse-val">{marketData.avg_turn_time_mf}d vs {marketData.avg_turn_time_industry}d</span>
              <span className="market-pulse-label">Our Turn vs Industry</span>
            </div>
          </div>
        </div>
      )}

      {/* Prospect Follow-Up Nudges */}
      {prospectFollowUps.length > 0 && (
        <div className="prospect-followups" data-testid="prospect-followups">
          <h3 className="prospect-followups-title">
            Follow-Up Needed <span className="prospect-followups-count">{prospectFollowUps.length}</span>
          </h3>
          <p className="prospect-followups-desc">These prospects were pitched 5+ days ago with no response. Time to follow up.</p>
          <div className="prospect-followups-list">
            {prospectFollowUps.map(f => {
              const prospect = prospects.find(p => p.prospect_id === f.prospect_id);
              const urgency = f.days_since >= 14 ? "critical" : f.days_since >= 7 ? "warning" : "info";
              return (
                <div key={f.prospect_id} className={`prospect-followup-item prospect-followup-${urgency}`} data-testid={`prospect-followup-${f.prospect_id}`}>
                  <div className="prospect-followup-info">
                    <strong>{prospect?.company || f.prospect_id}</strong>
                    <span className="prospect-followup-meta">Sent to {f.contact_email} &middot; {f.days_since} days ago</span>
                  </div>
                  <div className="prospect-followup-actions">
                    <span className={`prospect-followup-badge prospect-followup-badge-${urgency}`}>
                      {urgency === "critical" ? "Overdue" : urgency === "warning" ? "Stale" : "Nudge"}
                    </span>
                    {prospect && (
                      <button className="prospect-followup-btn" onClick={() => openPitchModal(prospect)} data-testid={`followup-pitch-${f.prospect_id}`}>
                        Follow Up
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === "todays-calls" && (
        <div className="todays-calls-view" data-testid="todays-calls-view">
          <div className="todays-calls-header">
            <div>
              <h3 style={{ margin: 0, fontSize: "1.1rem", color: "#00FFFF", letterSpacing: "0.04em" }}>Today&apos;s Calls</h3>
              <p style={{ margin: "4px 0 0", fontSize: "0.82rem", color: "#94A3B8" }}>
                Every active prospect — callbacks and meetings first, then most-recent activity. Tap any card to open the Call Brief.
              </p>
            </div>
            <button onClick={loadTodaysCalls} className="prospect-pitch-btn" style={{ fontSize: "0.75rem" }} data-testid="refresh-todays-calls">
              {todaysCallsLoading ? "Refreshing…" : "Refresh"}
            </button>
          </div>

          {todaysCallsLoading && todaysCalls.length === 0 && (
            <div className="prospect-pipeline-empty">Loading…</div>
          )}

          {!todaysCallsLoading && todaysCalls.length === 0 && (
            <div className="prospect-pipeline-empty" data-testid="todays-calls-empty">
              <p style={{ margin: 0 }}>No active calls yet. Move a prospect to <strong>Contacted</strong>, <strong>Responded</strong>, or <strong>Meeting</strong> on the Sales Pipeline view — or log a call note on any prospect — and they&apos;ll show up here.</p>
            </div>
          )}

          <div className="todays-calls-list">
            {todaysCalls.map(c => {
              const outcomeColor = {
                "callback": "#FFD580",
                "meeting_set": "#4ADE80",
                "walk_through_booked": "#4ADE80",
                "logged": "#00FFFF",
                "not_interested": "#FF8888",
                "wrong_number": "#FF8888"
              }[c.last_note_outcome] || "#94A3B8";
              const stageColor = (PIPELINE_STAGES.find(s => s.key === c.stage) || {}).color || "#888";
              return (
                <div key={c.prospect_id} className="todays-call-card" data-testid={`todays-call-${c.prospect_id}`}>
                  <div className="todays-call-top">
                    <div style={{ flex: 1 }}>
                      <strong className="todays-call-company">{c.company}</strong>
                      <div className="todays-call-meta">
                        {c.units > 0 && <span>{c.units.toLocaleString()} units</span>}
                        {c.target_floorplan && c.target_floorplan_vacancies != null && (
                          <span>{c.target_floorplan_vacancies} vacant in {c.target_floorplan}</span>
                        )}
                        <span style={{ color: stageColor }}>● {c.stage}</span>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      {c.primary_contact_phone && (
                        <a
                          href={`tel:${c.primary_contact_phone}`}
                          className="prospect-pitch-btn"
                          style={{ background: "linear-gradient(135deg, #00FFFF 0%, #00C8C8 100%)", color: "#0A1628", fontWeight: 700, textDecoration: "none" }}
                          data-testid={`todays-call-tel-${c.prospect_id}`}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <i className="fas fa-phone" style={{ marginRight: 6 }}></i>
                          {c.primary_contact_phone}
                        </a>
                      )}
                      <button
                        onClick={() => {
                          const fullProspect = prospects.find(p => p.prospect_id === c.prospect_id) || c;
                          setCallBriefProspect(fullProspect);
                        }}
                        className="prospect-pitch-btn"
                        data-testid={`todays-call-brief-${c.prospect_id}`}
                      >
                        Open Call Brief
                      </button>
                    </div>
                  </div>

                  {(c.primary_contact_name || c.primary_contact_role) && (
                    <div className="todays-call-contact">
                      <i className="fas fa-user" style={{ marginRight: 6, color: "#94A3B8" }}></i>
                      <strong>{c.primary_contact_name}</strong>
                      {c.primary_contact_role && <span style={{ color: "#94A3B8" }}> — {c.primary_contact_role}</span>}
                    </div>
                  )}

                  {c.last_note ? (
                    <div className="todays-call-lastnote" data-testid={`todays-call-lastnote-${c.prospect_id}`}>
                      <div className="todays-call-lastnote-meta">
                        <span className="todays-call-outcome" style={{ color: outcomeColor, borderColor: outcomeColor + "55" }}>
                          {(c.last_note_outcome || "logged").replace(/_/g, " ")}
                        </span>
                        <span className="todays-call-lastnote-when">
                          {c.last_note_at ? new Date(c.last_note_at).toLocaleString() : ""}
                        </span>
                      </div>
                      <p className="todays-call-lastnote-text">&ldquo;{c.last_note}&rdquo;</p>
                    </div>
                  ) : (
                    <div className="todays-call-lastnote todays-call-lastnote-empty">
                      <p style={{ margin: 0, color: "#94A3B8", fontSize: "0.82rem", fontStyle: "italic" }}>
                        No call notes yet. {c.pain_point ? c.pain_point.slice(0, 140) + (c.pain_point.length > 140 ? "…" : "") : "Open the Call Brief to start."}
                      </p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {viewMode === "pipeline" && (
        <div className="prospect-pipeline-board" data-testid="prospect-pipeline">
          {PIPELINE_STAGES.map(stage => {
            const stageProspects = prospects.filter(p => (pipelineMap[p.prospect_id] || "cold") === stage.key);
            return (
              <div key={stage.key} className="prospect-pipeline-col" data-testid={`pipeline-col-${stage.key}`}>
                <div className="prospect-pipeline-col-header" style={{ borderBottomColor: stage.color }}>
                  <span className="prospect-pipeline-stage">{stage.label}</span>
                  <span className="prospect-pipeline-count" style={{ background: stageProspects.length > 0 ? stage.color : "rgba(255,255,255,0.08)", color: stageProspects.length > 0 ? "#030B1A" : "#A0B3C8" }}>{stageProspects.length}</span>
                </div>
                <div className="prospect-pipeline-col-body">
                  {stageProspects.length === 0 && <div className="prospect-pipeline-empty">No prospects</div>}
                  {stageProspects.map(p => (
                    <div key={p.prospect_id} className="prospect-pipeline-card" data-testid={`pipeline-card-${p.prospect_id}`}>
                      <strong className="prospect-pipeline-card-name">{p.company}</strong>
                      {p.units > 0 && <span className="prospect-pipeline-card-units">{p.units.toLocaleString()} units</span>}
                      <span className="prospect-pipeline-card-tier" style={{ color: TIER_COLORS[p.tier] || "#888" }}>{TIER_LABELS[p.tier] || p.tier}</span>
                      <div className="prospect-pipeline-card-actions">
                        <select value={stage.key} onChange={e => moveProspect(p.prospect_id, e.target.value)} className="prospect-pipeline-select" data-testid={`pipeline-move-${p.prospect_id}`}>
                          {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                        </select>
                        <button className="prospect-pipeline-pitch-btn" onClick={() => openPitchModal(p)} data-testid={`pipeline-pitch-${p.prospect_id}`}>Pitch</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewMode === "tiers" && tiers.map(tier => (
        <div key={tier} className="prospects-tier" data-testid={`tier-${tier}`}>
          <h3 className="prospects-tier-title" style={{ borderLeftColor: TIER_COLORS[tier] || "#888" }}>
            <span className="prospects-tier-dot" style={{ background: TIER_COLORS[tier] || "#888" }}></span>
            {TIER_LABELS[tier] || tier}
          </h3>
          {prospects.filter(p => p.tier === tier).map(p => {
            const isExpanded = expanded === p.prospect_id;
            const wasSent = sentLog[p.prospect_id];
            return (
              <div key={p.prospect_id} className={`prospect-card ${isExpanded ? "prospect-card-open" : ""}`} data-testid={`prospect-${p.prospect_id}`}>
                <div className="prospect-card-header" onClick={() => setExpanded(isExpanded ? null : p.prospect_id)}>
                  <div className="prospect-card-left">
                    <strong className="prospect-name">{p.company}</strong>
                    {p.units > 0 && <span className="prospect-units">{p.units.toLocaleString()} units</span>}
                    {wasSent && <span className="prospect-sent-badge">Package Sent</span>}
                  </div>
                  <span className="prospect-status">{p.status}</span>
                </div>
                {isExpanded && (
                  <div className="prospect-detail">
                    <div className="prospect-info-grid">
                      <div className="prospect-info-item">
                        <span className="prospect-info-label">Address</span>
                        <span className="prospect-info-val">{p.address}</span>
                      </div>
                      <div className="prospect-info-item">
                        <span className="prospect-info-label">Decision Maker</span>
                        <span className="prospect-info-val">{p.decision_maker}</span>
                      </div>
                      {p.properties && (
                        <div className="prospect-info-item">
                          <span className="prospect-info-label">Properties</span>
                          <span className="prospect-info-val">{p.properties}</span>
                        </div>
                      )}
                    </div>
                    <div className="prospect-pain">
                      <span className="prospect-info-label">Opportunity</span>
                      <p>{p.pain_point}</p>
                    </div>
                    <div className="prospect-talk">
                      <span className="prospect-info-label">Approach</span>
                      <p>{p.talking_points}</p>
                    </div>
                    {wasSent && (
                      <div className="prospect-sent-info">
                        Sent to <strong>{wasSent.contact_email}</strong> on {new Date(wasSent.sent_at).toLocaleDateString()}
                      </div>
                    )}
                    <div className="prospect-action-btns">
                      <select value={pipelineMap[p.prospect_id] || "cold"} onChange={e => { e.stopPropagation(); moveProspect(p.prospect_id, e.target.value); }} className="prospect-stage-select" data-testid={`stage-select-${p.prospect_id}`}>
                        {PIPELINE_STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
                      </select>
                      {p.linkedin_dm && (
                        <button
                          className="prospect-pitch-btn"
                          style={{ background: "linear-gradient(135deg, #0A66C2 0%, #00FFFF 100%)", color: "#fff", fontWeight: 700 }}
                          onClick={(e) => {
                            e.stopPropagation();
                            navigator.clipboard.writeText(p.linkedin_dm).then(() => {
                              setLinkedinCopied(p.prospect_id);
                              setTimeout(() => setLinkedinCopied(null), 2200);
                            });
                          }}
                          data-testid={`copy-linkedin-${p.prospect_id}`}
                          title="Copies a ready-to-paste LinkedIn DM script for this prospect"
                        >
                          {linkedinCopied === p.prospect_id ? "✓ Copied — paste in LinkedIn" : "📨 Copy LinkedIn DM"}
                        </button>
                      )}
                      <button className="prospect-pitch-btn" onClick={(e) => { e.stopPropagation(); openPitchModal(p); }} data-testid={`generate-pitch-${p.prospect_id}`}>
                        Generate Pitch
                      </button>
                      <button
                        className="prospect-pitch-btn"
                        style={{ background: "linear-gradient(135deg, #00FFFF 0%, #00C8C8 100%)", color: "#0A1628", fontWeight: 700 }}
                        onClick={(e) => { e.stopPropagation(); setCallBriefProspect(p); }}
                        data-testid={`call-brief-btn-${p.prospect_id}`}
                        title="Open the Call Brief — contacts, live stats with auto-recalc bleed math, talking points, and call-note logger"
                      >
                        <i className="fas fa-phone" style={{ marginRight: 6 }}></i>
                        Call Brief
                      </button>
                      <button
                        className="prospect-pitch-btn"
                        style={{ background: "linear-gradient(135deg, #00DC78 0%, #00FFFF 100%)", color: "#030B1A" }}
                        onClick={(e) => { e.stopPropagation(); scheduleViaAutoPilot(p); }}
                        data-testid={`schedule-autopilot-${p.prospect_id}`}
                        title="Pre-fills the Auto-Pilot form with this prospect's contact, talking points, and ROI numbers"
                      >
                        📅 Schedule via Auto-Pilot
                      </button>
                      <button className="prospect-send-btn" onClick={(e) => { e.stopPropagation(); const c = parseContact(p); setSendModal(p.prospect_id); setSendForm({ contact_name: c.contact_name, contact_email: c.contact_email }); setSendResult(null); }} data-testid={`send-package-${p.prospect_id}`}>
                        {wasSent ? "Send Again" : "Send Package"}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {/* Send Modal */}
      {sendModal && (
        <div className="prospect-modal-overlay" onClick={() => setSendModal(null)}>
          <div className="prospect-modal" onClick={e => e.stopPropagation()} data-testid="send-package-modal">
            <h3>Send Outreach Package</h3>
            <p className="prospect-modal-sub">{prospects.find(p => p.prospect_id === sendModal)?.company}</p>
            <form onSubmit={sendPackage}>
              <input type="text" placeholder="Contact Name (e.g., Regional Director)" value={sendForm.contact_name} onChange={e => setSendForm(f => ({ ...f, contact_name: e.target.value }))} className="prospect-modal-input" data-testid="package-contact-name" />
              <input type="email" placeholder="Contact Email" value={sendForm.contact_email} onChange={e => setSendForm(f => ({ ...f, contact_email: e.target.value }))} className="prospect-modal-input" required data-testid="package-contact-email" />
              {sendResult === "sent" && <div className="prospect-result prospect-result-ok">Package sent successfully!</div>}
              {sendResult === "logged" && <div className="prospect-result prospect-result-warn">Package logged (email delivery pending — verify Resend domain)</div>}
              {sendResult === "error" && <div className="prospect-result prospect-result-err">Failed to send. Try again.</div>}
              <div className="prospect-modal-btns">
                <button type="button" onClick={() => setSendModal(null)} className="prospect-modal-cancel">Cancel</button>
                <button type="submit" disabled={sending} className="prospect-modal-send" data-testid="package-send-btn">{sending ? "Sending..." : "Send Package"}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Generate Pitch Modal */}
      {pitchModal && pitchData && (
        <div className="prospect-modal-overlay" onClick={() => setPitchModal(null)}>
          <div className="prospect-modal pitch-gen-modal" onClick={e => e.stopPropagation()} data-testid="pitch-gen-modal">
            <div className="pitch-gen-header">
              <h3>Personalized Pitch</h3>
              <span className="pitch-gen-company">{pitchModal.company}</span>
            </div>
            {pitchData.units > 0 && (
              <div className="pitch-gen-stats" data-testid="pitch-gen-stats">
                <div className="pitch-stat">
                  <span className="pitch-stat-val">{pitchData.units.toLocaleString()}</span>
                  <span className="pitch-stat-label">Units</span>
                </div>
                <div className="pitch-stat">
                  <span className="pitch-stat-val">${Math.round(pitchData.units * (AVG_RENT_DFW / 30)).toLocaleString()}</span>
                  <span className="pitch-stat-label">Daily Vacancy Loss</span>
                </div>
                <div className="pitch-stat">
                  <span className="pitch-stat-val">${(pitchData.units * AVG_RENT_DFW).toLocaleString()}</span>
                  <span className="pitch-stat-label">Monthly Loss</span>
                </div>
              </div>
            )}
            <div className="pitch-gen-subject" data-testid="pitch-gen-subject">
              <span className="prospect-info-label">Subject Line</span>
              <p>{pitchData.subject}</p>
            </div>
            <div className="pitch-gen-body" data-testid="pitch-gen-body">
              <span className="prospect-info-label">Email Body</span>
              <pre className="pitch-gen-text">{pitchData.body}</pre>
            </div>

            {!pitchEmailMode ? (
              <div className="pitch-gen-actions">
                <button className="pitch-gen-copy" onClick={() => { navigator.clipboard.writeText(`Subject: ${pitchData.subject}\n\n${pitchData.body}`).then(() => { setPitchCopied(true); setTimeout(() => setPitchCopied(false), 2000); }); }} data-testid="pitch-copy-btn">
                  {pitchCopied ? "Copied!" : "Copy to Clipboard"}
                </button>
                <button className="pitch-gen-email" onClick={() => { const c = pitchModal ? parseContact(pitchModal) : {}; setPitchEmailForm({ to_email: c.contact_email || pitchEmailForm.to_email || "" }); setPitchEmailMode(true); }} data-testid="pitch-email-btn">
                  Send via Email
                </button>
                <button className="prospect-modal-cancel" onClick={() => setPitchModal(null)}>Close</button>
              </div>
            ) : (
              <form onSubmit={sendPitchEmail} className="pitch-gen-email-form">
                <input type="email" placeholder="Recipient Email" value={pitchEmailForm.to_email} onChange={e => setPitchEmailForm({ to_email: e.target.value })} className="prospect-modal-input" required data-testid="pitch-email-input" />
                {pitchEmailResult === "sent" && <div className="prospect-result prospect-result-ok">Pitch sent successfully!</div>}
                {pitchEmailResult === "forwarded" && <div className="prospect-result prospect-result-ok">Domain not verified yet — use "Copy to Clipboard" and paste into Gmail to send.</div>}
                {pitchEmailResult === "logged" && <div className="prospect-result prospect-result-warn">Domain not verified yet — use "Copy to Clipboard" and paste into Gmail to send.</div>}
                {pitchEmailResult === "error" && <div className="prospect-result prospect-result-err">Failed to send. Use "Copy to Clipboard" and paste into Gmail.</div>}
                <div className="pitch-gen-actions">
                  <button type="submit" disabled={pitchSending} className="pitch-gen-email" data-testid="pitch-send-btn">{pitchSending ? "Sending..." : "Send Pitch"}</button>
                  <button type="button" className="prospect-modal-cancel" onClick={() => setPitchEmailMode(false)}>Back</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* Add Prospect Modal */}
      {showAddProspect && (
        <div className="cb-overlay" onClick={() => setShowAddProspect(false)} data-testid="add-prospect-modal">
          <div className="cb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 640 }}>
            <div className="cb-header">
              <div>
                <div className="cb-eyebrow">Add Prospect</div>
                <h2 className="cb-company">New Lead</h2>
                <div className="cb-meta"><span>Captured by Shannon &middot; minimum required: company name</span></div>
              </div>
              <button onClick={() => setShowAddProspect(false)} className="cb-close" data-testid="close-add-prospect">✕</button>
            </div>

            <div className="cb-section">
              <div className="cb-section-title">Company</div>
              <div className="cb-stat-grid">
                <div className="cb-stat-field" style={{ gridColumn: "span 2" }}>
                  <label className="cb-stat-label">Company / Property Name *</label>
                  <div className="cb-stat-input-wrap">
                    <input type="text" value={newProspect.company} onChange={(e) => setNewProspect({...newProspect, company: e.target.value})} className="cb-stat-input" placeholder="e.g. Westmount at River Park" data-testid="prospect-company" />
                  </div>
                </div>
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Tier</label>
                  <div className="cb-stat-input-wrap">
                    <select value={newProspect.tier} onChange={(e) => setNewProspect({...newProspect, tier: e.target.value})} className="cb-stat-input" data-testid="prospect-tier">
                      <option value="institutional">Institutional PM (1000+ units)</option>
                      <option value="local_pm">Local PM / Owner-Operator</option>
                      <option value="sfr">Single-Family Rentals</option>
                      <option value="distressed">Distressed / Receivership</option>
                      <option value="grassroots">Grassroots / Networking</option>
                      <option value="airbnb">Airbnb / STR Host</option>
                      <option value="commercial">Commercial Cleaning</option>
                      <option value="residential">Residential Cleaning</option>
                    </select>
                  </div>
                </div>
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Units</label>
                  <div className="cb-stat-input-wrap">
                    <input type="number" value={newProspect.units} onChange={(e) => setNewProspect({...newProspect, units: e.target.value})} className="cb-stat-input" placeholder="280" data-testid="prospect-units" />
                  </div>
                </div>
                <div className="cb-stat-field" style={{ gridColumn: "span 2" }}>
                  <label className="cb-stat-label">Property Address</label>
                  <div className="cb-stat-input-wrap">
                    <input type="text" value={newProspect.address} onChange={(e) => setNewProspect({...newProspect, address: e.target.value})} className="cb-stat-input" placeholder="3309 River Road Court, Fort Worth, TX 76116" data-testid="prospect-address" />
                  </div>
                </div>
              </div>
            </div>

            <div className="cb-section">
              <div className="cb-section-title">Primary Contact (optional — fills Call Brief)</div>
              <div className="cb-stat-grid">
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Contact Name</label>
                  <div className="cb-stat-input-wrap">
                    <input type="text" value={newProspect.primary_contact_name} onChange={(e) => setNewProspect({...newProspect, primary_contact_name: e.target.value})} className="cb-stat-input" placeholder="Brittany Miller" data-testid="prospect-contact-name" />
                  </div>
                </div>
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Title / Role</label>
                  <div className="cb-stat-input-wrap">
                    <input type="text" value={newProspect.primary_contact_role} onChange={(e) => setNewProspect({...newProspect, primary_contact_role: e.target.value})} className="cb-stat-input" placeholder="Property Manager" data-testid="prospect-contact-role" />
                  </div>
                </div>
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Phone</label>
                  <div className="cb-stat-input-wrap">
                    <input type="tel" value={newProspect.primary_contact_phone} onChange={(e) => setNewProspect({...newProspect, primary_contact_phone: e.target.value})} className="cb-stat-input" placeholder="817-916-8991" data-testid="prospect-contact-phone" />
                  </div>
                </div>
                <div className="cb-stat-field">
                  <label className="cb-stat-label">Email</label>
                  <div className="cb-stat-input-wrap">
                    <input type="email" value={newProspect.primary_contact_email} onChange={(e) => setNewProspect({...newProspect, primary_contact_email: e.target.value})} className="cb-stat-input" placeholder="brittany.miller@livebh.com" data-testid="prospect-contact-email" />
                  </div>
                </div>
              </div>
            </div>

            <div className="cb-section">
              <div className="cb-section-title">Intel</div>
              <div className="cb-stat-field" style={{ marginBottom: 12 }}>
                <label className="cb-stat-label">Status / Current Situation</label>
                <textarea value={newProspect.status} onChange={(e) => setNewProspect({...newProspect, status: e.target.value})} className="cb-intel-textarea" rows={2} placeholder="29 vacancies — 12 in B1 floor plan. Offering 1-month-free. Class B, built 1984." data-testid="prospect-status" />
              </div>
              <div className="cb-stat-field" style={{ marginBottom: 12 }}>
                <label className="cb-stat-label">Pain Point</label>
                <textarea value={newProspect.pain_point} onChange={(e) => setNewProspect({...newProspect, pain_point: e.target.value})} className="cb-intel-textarea" rows={2} placeholder="$19,400/month concession drag on B1. 18% rent gap vs submarket." data-testid="prospect-pain" />
              </div>
              <div className="cb-stat-field">
                <label className="cb-stat-label">Talking Points / Strategy</label>
                <textarea value={newProspect.talking_points} onChange={(e) => setNewProspect({...newProspect, talking_points: e.target.value})} className="cb-intel-textarea" rows={3} placeholder="Brittany hears OPERATIONS RELIEF. Chasidy hears NOI EXPANSION. Pitch 3-unit B1 pilot." data-testid="prospect-talking" />
              </div>
            </div>

            <div className="cb-section" style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setShowAddProspect(false)} className="cb-clear-btn" style={{ position: "static", background: "rgba(255,255,255,0.1)", color: "#94A3B8", padding: "10px 18px" }} data-testid="cancel-add-prospect">
                Cancel
              </button>
              <button onClick={saveNewProspect} disabled={savingProspect || !newProspect.company.trim()} className="cb-note-save" data-testid="save-new-prospect">
                {savingProspect ? "Saving…" : "Save Prospect"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call Brief modal — single mobile-friendly screen with live stats + auto-recalc bleed + call-note logger */}
      {callBriefProspect && (
        <CallBrief
          prospect={callBriefProspect}
          onClose={() => setCallBriefProspect(null)}
        />
      )}
    </div>
  );
};

export default ProspectsTab;
